import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface DeleteTicketResponse {
  success: boolean;
  data: {
    ticketId: string;
    deleted: boolean;
    deletedAt: string;
    deletedBy: string;
  };
}

async function deleteTicket(ticketId: string, token: string | undefined, workspaceId: string) {
  const bearer = resolveBearerToken(token);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.delete<DeleteTicketResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/${ticketId}`,
    { headers },
  );

  return response.data;
}

export function useDeleteTicket() {
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    // Try id first, then SK, then extract from SK if it starts with "WS#"
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      // If SK starts with "WS#", extract the UUID part
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3); // Remove "WS#" prefix
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: (ticketId: string) => {
      console.log('🗑️ Deleting ticket:', ticketId, 'from workspace:', workspaceId);
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      return deleteTicket(ticketId, user?.signInUserSession?.idToken?.jwtToken, workspaceId);
    },
    onMutate: async (ticketId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries([queryKeys.tickets, workspaceId]);

      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData([queryKeys.tickets, workspaceId]);

      // Optimistically remove the ticket from cache
      queryClient.setQueryData([queryKeys.tickets, workspaceId], (old: any) => {
        if (!old) return old;

        console.log('🔮 Optimistic delete: Removing ticket', ticketId);

        // Remove ticket from tickets array
        const newTickets = old.tickets.filter((t: any) => t.ticketId !== ticketId && t.id !== ticketId);

        // Remove ticket from column's ticketOrder array
        const newColumns = old.columns.map((col: any) => {
          if (col.ticketOrder && col.ticketOrder.includes(ticketId)) {
            return {
              ...col,
              ticketOrder: col.ticketOrder.filter((id: string) => id !== ticketId),
            };
          }
          return col;
        });

        console.log(`🔮 Optimistic: Removed ticket ${ticketId} from UI`);
        return { ...old, tickets: newTickets, columns: newColumns };
      });

      // Return context with the snapshot
      return { previousTickets };
    },
    onSuccess: async (_data, ticketId) => {
      trackEvent('ticket_deleted', { ticket_id: ticketId });
      console.log('✅ Ticket deleted successfully:', ticketId);
      console.log('🔄 Invalidating tickets query for workspace:', workspaceId);

      // Invalidate tickets query - React Query will refetch and sync with server
      await queryClient.invalidateQueries([queryKeys.tickets, workspaceId]);

      console.log('✅ Query invalidated, refetch will happen automatically');
    },
    onError: (error: any, ticketId, context: any) => {
      console.error('❌ Ticket deletion failed:', error);

      // Rollback to previous state on error
      if (context?.previousTickets) {
        queryClient.setQueryData([queryKeys.tickets, workspaceId], context.previousTickets);
        console.log('🔙 Rolled back to previous state due to error');
      }

      // Handle specific error cases
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please log in again.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else if (error.response?.status === 404) {
        throw new Error('Ticket not found.');
      } else if (error.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(error.message || 'Failed to delete ticket. Please try again.');
      }
    },
  });
}
